import React, { useMemo, useState } from 'react';
import { Alert, Button, Card, Empty, Space, Spin, Tag, Typography, message } from 'antd';
import { useApiRequest } from '../../hooks/useApiRequest';
import { useResource } from '../../hooks/useResource';
import LearnerCourseModal from './LearnerCourseModal';
import { openServerUrl, preopenWindow, userMessageForError } from '../../utils/apiError';
import { reportOpenFailure } from '../../utils/openLinkFeedback';
import { buildEnrollmentRows, attemptsLeft, certificateIssueText, formatDdMmYyyy, formatScore } from '../../utils/trainingModel';

const { Text } = Typography;

/**
 * "My training" for Company SPOC / Company Delegate. The API returns a learner only published
 * courses and only their OWN enrolments, so nothing is filtered here in the client.
 * Learners cannot enrol themselves — an Advisor does — so an empty list says exactly that.
 */
const LearnerTraining = ({ projectId, readOnly = false }) => {
    const { request } = useApiRequest();
    const [active, setActive] = useState(null);
    const [certBusy, setCertBusy] = useState(null);

    const courses = useResource(
        async () => (await request('GET', `/projects/${projectId}/training/courses`)).data.courses || [],
        [projectId, request],
        { enabled: !readOnly },
    );
    const enrollments = useResource(
        async () => (await request('GET', `/projects/${projectId}/training/enrollments`)).data.enrollments || [],
        [projectId, request],
    );

    const rows = useMemo(
        () => buildEnrollmentRows(enrollments.data, courses.data, []),
        [enrollments.data, courses.data],
    );

    const reloadAll = () => {
        enrollments.reload();
        courses.reload();
    };

    const openCertificate = async (row) => {
        setCertBusy(row.id);
        const win = preopenWindow();
        try {
            const { data } = await request('GET', `/projects/${projectId}/training/enrollments/${row.id}/certificate`);
            if (!openServerUrl(data?.url, win)) reportOpenFailure(message, data?.url);
        } catch (err) {
            if (win && !win.closed) win.close();
            message.error(userMessageForError(err, 'Could not open the certificate.'));
        } finally {
            setCertBusy(null);
        }
    };

    const retryCertificate = async (row) => {
        setCertBusy(row.id);
        try {
            const { data } = await request('POST', `/projects/${projectId}/training/enrollments/${row.id}/certificate/retry`);
            if (data.certificateStatus === 'issued') message.success('Certificate issued.');
            else message.warning(data.certificateWarning || 'The certificate still could not be generated.');
            enrollments.reload();
        } catch (err) {
            message.error(err.message || 'Could not retry the certificate.');
        } finally {
            setCertBusy(null);
        }
    };

    const loading = (!readOnly && courses.status === 'loading' && !courses.data) || (enrollments.status === 'loading' && !enrollments.data);
    const failed = !readOnly && courses.status === 'error' ? courses : enrollments.status === 'error' ? enrollments : null;

    return (
        <div>
            {loading && !failed && <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>}
            {failed && (
                <Alert
                    type="error"
                    showIcon
                    message="Could not load your training"
                    description={failed.error?.message}
                    action={<Button size="small" onClick={reloadAll}>Retry</Button>}
                />
            )}
            {!loading && !failed && rows.length === 0 && (
                <Empty description="You have not been enrolled in any training yet. Your Advisor enrols learners once a course is published." />
            )}
            {!failed && rows.map((row) => (
                <Card key={row.id} size="small" style={{ marginBottom: 12 }} data-testid={`my-training-${row.id}`}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                        <div>
                            <Text strong>{row.courseTitle}</Text> <Tag>v{row.courseVersion}</Tag>
                            <div>
                                {row.completed ? (
                                    <Space wrap>
                                        {row.completed && !row.hasCertificate && (
                                            <Text type="danger" style={{ fontSize: 12 }}>{certificateIssueText(row.certificateIssue)}</Text>
                                        )}
                                        <Tag color="green">Completed {formatDdMmYyyy(row.completedAt)}</Tag>
                                        {row.score && <Text>Result: {formatScore(row.score)}</Text>}
                                    </Space>
                                ) : (
                                    <Space wrap>
                                        {row.attempted ? <Tag color="red">Not passed</Tag> : <Tag color="orange">Not completed</Tag>}
                                        {row.attempted ? (
                                            <Text type="secondary">Last attempt: {formatScore(row.score) === '-' ? 'not passed' : formatScore(row.score)}{attemptsLeft(row.maxAttempts, row.attemptCount) !== null ? ` · attempts remaining: ${attemptsLeft(row.maxAttempts, row.attemptCount)}` : ''}</Text>
                                        ) : (
                                            <Text type="secondary">Enrolled {formatDdMmYyyy(row.enrolledAt)}</Text>
                                        )}
                                    </Space>
                                )}
                            </div>
                        </div>
                        <Space wrap>
                            {row.hasCertificate && (
                                <Button loading={certBusy === row.id} onClick={() => openCertificate(row)}>Certificate</Button>
                            )}
                            {row.completed && !row.hasCertificate && (
                                <Button loading={certBusy === row.id} onClick={() => retryCertificate(row)}>Retry certificate</Button>
                            )}
                            {!readOnly && (
                                <Button type={row.completed ? 'default' : 'primary'} onClick={() => setActive(enrollments.data.find((e) => e.id === row.id))}>
                                    {row.completed ? 'Review course' : row.attempted ? 'Retry training' : 'Start training'}
                                </Button>
                            )}
                        </Space>
                    </div>
                </Card>
            ))}
            <LearnerCourseModal
                open={!!active}
                projectId={projectId}
                enrollment={active}
                onClose={() => setActive(null)}
                onCompleted={reloadAll}
            />
        </div>
    );
};

export default LearnerTraining;
